import { useCallback, useEffect, useState } from 'react';
import { Button, Heading, Select, Spinner, Table } from '@vwfs-bronson/bronson-react';
import { AuthTokens } from '../../auth';
import { FesConfig, User, getUsers, updateUserTeams } from '../../fes';

export function TeamMembersPanel({
  fesConfig,
  tokens,
  teamId,
}: {
  fesConfig: FesConfig;
  tokens: AuthTokens;
  teamId: string;
}) {
  const [users, setUsers] = useState<User[]>();
  const [error, setError] = useState<string>();
  const [pendingUserId, setPendingUserId] = useState<string>();
  const [selectedUserId, setSelectedUserId] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const response = await getUsers(fesConfig, tokens);
      setUsers(response.data || []);
      setError(undefined);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Unable to load users.');
    }
  }, [fesConfig, tokens]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const setMembership = async (user: User, isMember: boolean) => {
    const current = user.teamIds ?? [];
    const next = isMember
      ? Array.from(new Set([...current, teamId]))
      : current.filter(id => id !== teamId);

    setPendingUserId(user.userId);
    setError(undefined);

    try {
      const response = await updateUserTeams(fesConfig, tokens, user.userId, next);
      const updated = response.data;

      setUsers(latest =>
        (latest || []).map(candidate =>
          candidate.userId === user.userId ? { ...candidate, teamIds: updated?.teamIds ?? next } : candidate
        )
      );
      setSelectedUserId('');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to update team membership.');
    } finally {
      setPendingUserId(undefined);
    }
  };

  if (!users && !error) {
    return (
      <section className="app-team-panel" aria-labelledby="team-members-heading">
        <Heading level={2} id="team-members-heading" className="app-panel-title">
          Members
        </Heading>
        <Spinner center small />
      </section>
    );
  }

  const allUsers = users || [];
  const members = allUsers.filter(user => (user.teamIds ?? []).includes(teamId));
  const candidates = allUsers.filter(user => !(user.teamIds ?? []).includes(teamId));
  const label = (user: User) => user.email || user.userId;

  return (
    <section className="app-team-panel" aria-labelledby="team-members-heading">
      <div className="app-team-panel-header">
        <div>
          <div className="app-kicker">Team Detail</div>
          <Heading level={2} id="team-members-heading" className="app-panel-title">
            Members
          </Heading>
        </div>
      </div>

      {error ? (
        <div className="app-result app-team-error" role="alert">
          {error}
        </div>
      ) : null}

      {members.length === 0 ? (
        <p className="app-empty">No members yet.</p>
      ) : (
        <div className="app-table-wrap">
          <Table className="app-table" colored>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Other teams</Table.Th>
                <Table.Th>{''}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {members.map(user => (
                <Table.Tr key={user.userId}>
                  <Table.Td>{label(user)}</Table.Td>
                  <Table.Td>{(user.roles ?? []).join(', ') || '-'}</Table.Td>
                  <Table.Td>{Math.max(0, (user.teamIds ?? []).length - 1)}</Table.Td>
                  <Table.Td>
                    <Button
                      secondary
                      small
                      disabled={pendingUserId === user.userId}
                      onClick={() => setMembership(user, false)}
                    >
                      {pendingUserId === user.userId ? 'Removing...' : 'Remove'}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      <div className="app-table-toolbar">
        <Select
          aria-label="User to add"
          value={selectedUserId}
          onChange={event => setSelectedUserId(event.target.value)}
        >
          <Select.Item value="">Select a user</Select.Item>
          {candidates.map(user => (
            <Select.Item key={user.userId} value={user.userId}>
              {label(user)}
            </Select.Item>
          ))}
        </Select>
        <Button
          disabled={!selectedUserId || Boolean(pendingUserId)}
          onClick={() => {
            const user = candidates.find(candidate => candidate.userId === selectedUserId);
            if (user) {
              void setMembership(user, true);
            }
          }}
        >
          Add member
        </Button>
      </div>
      {candidates.length === 0 && allUsers.length > 0 ? (
        <p className="app-empty">
          Every user who has signed in is already a member. Users appear here only after their first
          sign-in.
        </p>
      ) : null}
    </section>
  );
}
